import React, { useState, useEffect, useCallback } from 'react';
import { Download, List } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';
import api from '../../services/api';
import { field, ghost, primary, muted, card, rowBorder, hm, errorText, addDays, dayLabel, plural } from './styles';
import { Message, StatusBadge } from './ui';

const pad = (n) => String(n).padStart(2, '0');
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const mondayOf = (key) => { const d = new Date(`${key}T00:00:00Z`); return addDays(key, -((d.getUTCDay() + 6) % 7)); };
const monthStart = (key) => `${key.slice(0, 8)}01`;
const monthEnd = (key) => { const d = new Date(`${monthStart(key)}T00:00:00Z`); d.setUTCMonth(d.getUTCMonth() + 1); d.setUTCDate(0); return d.toISOString().slice(0, 10); };

const RANGES = {
    thisWeek: { label: 'This week', range: (t) => [mondayOf(t), t] },
    lastWeek: { label: 'Last week', range: (t) => [addDays(mondayOf(t), -7), addDays(mondayOf(t), -1)] },
    thisMonth: { label: 'This month', range: (t) => [monthStart(t), t] },
    lastMonth: { label: 'Last month', range: (t) => { const end = addDays(monthStart(t), -1); return [monthStart(end), monthEnd(end)]; } },
    custom: { label: 'Custom' },
};
const GROUPS = [['project', 'Project'], ['client', 'Client'], ['user', 'Person'], ['task', 'Task'], ['day', 'Day']];
const time = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const Stat = ({ label, value, sub }) => (
    <div className="card" style={{ padding: '14px 18px', flex: '1 1 150px', marginBottom: 0 }}>
        <div style={muted}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-main)' }}>{value}</div>
        {sub && <div style={muted}>{sub}</div>}
    </div>
);

const Reports = () => {
    const today = todayKey();
    const [preset, setPreset] = useState('thisWeek');
    const [from, setFrom] = useState(mondayOf(today));
    const [to, setTo] = useState(today);
    const [groupBy, setGroupBy] = useState('project');
    const [filters, setFilters] = useState({ clientId: '', projectId: '', userId: '', billable: '' });
    const [options, setOptions] = useState({ users: [], projects: [], clients: [], scope: 'SELF' });
    const [summary, setSummary] = useState(null);
    const [entries, setEntries] = useState(null);
    const [showEntries, setShowEntries] = useState(false);
    const [message, setMessage] = useState(null);

    const params = useCallback(() => {
        const p = new URLSearchParams({ from, to, groupBy });
        Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
        return p.toString();
    }, [from, to, groupBy, filters]);

    useEffect(() => {
        api.get('/time/reports/options').then(r => setOptions(r.data))
            .catch(e => setMessage({ ok: false, text: errorText(e, 'Failed to load report filters') }));
    }, []);

    const load = useCallback(async () => {
        try {
            const qs = params();
            const s = await api.get(`/time/reports/summary?${qs}`);
            setSummary(s.data);
            setEntries(showEntries ? (await api.get(`/time/reports/entries?${qs}`)).data : null);
            setMessage(null);
        } catch (e) {
            setMessage({ ok: false, text: errorText(e, 'Failed to build the report') });
        }
    }, [params, showEntries]);

    useEffect(() => { load(); }, [load]);

    const choosePreset = (key) => {
        setPreset(key);
        if (RANGES[key].range) { const [f, t] = RANGES[key].range(today); setFrom(f); setTo(t); }
    };

    const exportCsv = async () => {
        try {
            const res = await api.get(`/time/reports/entries.csv?${params()}`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `time-report_${from}_to_${to}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            setMessage({ ok: false, text: errorText(e, 'Export failed') });
        }
    };

    const projectsForClient = options.projects.filter(p => !filters.clientId || (filters.clientId === 'none' ? !p.clientId : p.clientId === filters.clientId));
    const t = summary?.totals;
    const max = summary ? Math.max(1, ...summary.rows.map(r => r.totalSec)) : 1;
    const scopeText = { ALL: 'Everyone in the organisation.', TEAM: 'You, the people who report to you, and all time on projects you manage.', SELF: 'Your own time.' }[options.scope];
    const rowLabel = (r) => groupBy === 'day' ? dayLabel(r.key, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : r.label;

    return (
        <div className="container" style={{ padding: '2rem 1rem', maxWidth: 1100 }}>
            <PageHeader crumbs={crumbsFor('timeReports')} backTo="/time" backLabel="Time Tracker" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h1 style={{ margin: 0, flex: 1 }}>Time Reports</h1>
                <button className="btn btn-primary" style={primary} onClick={exportCsv} disabled={!t || t.entries === 0}><Download size={14} /> Export CSV</button>
            </div>
            <p style={{ ...muted, fontSize: 15 }}>Showing: {scopeText}</p>
            <Message message={message} />

            <section className="card" style={{ ...card, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Period</span>
                    <select aria-label="Period" style={field} value={preset} onChange={e => choosePreset(e.target.value)}>
                        {Object.entries(RANGES).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
                    </select>
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>From</span>
                    <input aria-label="From" type="date" style={field} value={from} max={to} onChange={e => { setPreset('custom'); setFrom(e.target.value); }} />
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>To</span>
                    <input aria-label="To" type="date" style={field} value={to} min={from} onChange={e => { setPreset('custom'); setTo(e.target.value); }} />
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Group by</span>
                    <select aria-label="Group by" style={field} value={groupBy} onChange={e => setGroupBy(e.target.value)}>
                        {GROUPS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                </label>
                {options.clients.length > 0 && (
                    <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Client</span>
                        <select aria-label="Client filter" style={field} value={filters.clientId} onChange={e => setFilters({ ...filters, clientId: e.target.value, projectId: '' })}>
                            <option value="">All clients</option>
                            <option value="none">Internal (no client)</option>
                            {options.clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </label>
                )}
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Project</span>
                    <select aria-label="Project filter" style={field} value={filters.projectId} onChange={e => setFilters({ ...filters, projectId: e.target.value })}>
                        <option value="">All projects</option>
                        {projectsForClient.map(p => <option key={p.id} value={p.id}>{p.name}{p.isActive ? '' : ' (archived)'}</option>)}
                    </select>
                </label>
                {options.users.length > 1 && (
                    <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Person</span>
                        <select aria-label="Person filter" style={field} value={filters.userId} onChange={e => setFilters({ ...filters, userId: e.target.value })}>
                            <option value="">Everyone</option>
                            {options.users.map(u => <option key={u.id} value={u.id}>{u.name}{u.isActive ? '' : ' (inactive)'}</option>)}
                        </select>
                    </label>
                )}
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Billable</span>
                    <select aria-label="Billable filter" style={field} value={filters.billable} onChange={e => setFilters({ ...filters, billable: e.target.value })}>
                        <option value="">All time</option>
                        <option value="true">Billable only</option>
                        <option value="false">Non-billable only</option>
                    </select>
                </label>
            </section>

            {t && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                    <Stat label="Total time" value={hm(t.totalSec)} sub={plural(t.entries, 'entry').replace('entrys', 'entries')} />
                    <Stat label="Billable" value={hm(t.billableSec)} sub={t.totalSec ? `${Math.round((t.billableSec / t.totalSec) * 100)}% of total` : '—'} />
                    <Stat label="Non-billable" value={hm(t.totalSec - t.billableSec)} />
                    <Stat label="People" value={t.people} sub={t.needsReview ? `${t.needsReview} entr${t.needsReview === 1 ? 'y needs' : 'ies need'} review` : null} />
                </div>
            )}

            {summary && (
                <section className="card" style={card}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                        <h3 style={{ margin: 0, flex: 1 }}>By {GROUPS.find(g => g[0] === groupBy)[1].toLowerCase()}</h3>
                        <button style={ghost} onClick={() => setShowEntries(!showEntries)}><List size={13} /> {showEntries ? 'Hide entries' : 'Show entries'}</button>
                    </div>
                    {summary.rows.length === 0 && <p style={muted}>No time logged for these filters.</p>}
                    {summary.rows.map(r => (
                        <div key={r.key} data-testid="report-row" style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 2fr) 3fr 90px 110px', gap: 12, alignItems: 'center', padding: '8px 0', borderTop: rowBorder }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-main)' }}>
                                {r.color && <span style={{ width: 10, height: 10, borderRadius: '50%', background: r.color, flexShrink: 0 }} />}{rowLabel(r)}
                            </span>
                            <div style={{ background: 'var(--accent-subtle)', borderRadius: 4, height: 10, overflow: 'hidden' }} title={`${hm(r.billableSec)} billable`}>
                                <div style={{ width: `${(r.totalSec / max) * 100}%`, height: '100%', background: 'var(--primary)', opacity: 0.35, position: 'relative' }}>
                                    <div style={{ width: `${r.totalSec ? (r.billableSec / r.totalSec) * 100 : 0}%`, height: '100%', background: 'var(--primary)' }} />
                                </div>
                            </div>
                            <strong style={{ textAlign: 'right' }}>{hm(r.totalSec)}</strong>
                            <span style={{ ...muted, textAlign: 'right' }}>{hm(r.billableSec)} billable</span>
                        </div>
                    ))}
                    {summary.truncated && <p style={{ ...muted, color: '#d97706' }}>Very large range: only the first 50,000 entries are included. Narrow the dates.</p>}
                </section>
            )}

            {showEntries && entries && (
                <section className="card" style={{ ...card, overflowX: 'auto' }}>
                    <h3 style={{ marginTop: 0 }}>Entries</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                        <thead>
                            <tr style={{ textAlign: 'left', ...muted }}>
                                {['Date', 'Person', 'Project', 'Description', 'Time', 'Duration', 'Billable', 'Timesheet'].map(h => <th key={h} style={{ padding: '6px 8px', fontWeight: 600 }}>{h}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {entries.entries.map(e => (
                                <tr key={e.id} style={{ borderTop: rowBorder }}>
                                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{dayLabel(e.workDate.slice(0, 10), { day: 'numeric', month: 'short' })}</td>
                                    <td style={{ padding: '6px 8px' }}>{e.user?.name}</td>
                                    <td style={{ padding: '6px 8px' }}>{e.project?.client ? `${e.project.client.name} · ` : ''}{e.project?.name}{e.task ? ` › ${e.task.name}` : ''}</td>
                                    <td style={{ padding: '6px 8px' }}>{e.description}{e.needsReview && <span style={{ color: '#d97706' }}> ⚠ needs review</span>}</td>
                                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', ...muted }}>{time(e.startedAt)}–{time(e.endedAt)}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}><strong>{hm(e.durationSec)}</strong></td>
                                    <td style={{ padding: '6px 8px' }}>{e.isBillable ? 'Yes' : 'No'}</td>
                                    <td style={{ padding: '6px 8px' }}><StatusBadge status={e.timesheet?.status || 'DRAFT'} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {entries.truncated && <p style={{ ...muted, color: '#d97706' }}>Showing the first {entries.limit} entries. Export CSV for all of them.</p>}
                </section>
            )}
        </div>
    );
};

export default Reports;
